// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue'
import Notifications from 'vue-notification'
import App from './App'
import router from './router'
import store from './store'
import VueClipboard from 'vue-clipboard2'
import ToggleSwitchControl from './componentsReusable/ToggleSwitchControl.vue'
import CheckboxControl from './componentsReusable/CheckboxControl.vue'
import Vue2Filters from 'vue2-filters'
import {InvoiceStatus} from '@/enums/invoiceStatusEnum';
import format from 'date-fns/format';
import {convertDateStringToUTCDate} from "@/utils/dateUtil";
import {DateTime} from "luxon";

Vue.use(Vue2Filters);
Vue.use(Notifications);
Vue.use(VueClipboard);

// Global mixins aren't ideal but in this case it will do the job nicely
Vue.mixin({
  data () {
    return {
      nmrFlatpickrConfig: {
        allowInput: true,
        altInput: true,
        altFormat: 'm/d/Y',
        locale: {
          'firstDayOfWeek': 1 // start week on Monday
        },
        onClose: function (selectedDates:any, dateStr:any, instance:any) {
          // This sets the date to what is in the input box when tabbing off of the date.
          instance.setDate(instance._input.value, true, instance.config.altFormat);
          // This is a workaround to flatpickr not updating off of any user input when the calendar picker widget itself is
          // closed.  This forces the user to click back into the element if they wish to manually enter a date, which also
          // opens up the calendar widget, which then allows for updates based on manual input.
          instance._input.blur();
        },
        parseDate: (date: string, format: any) => {
          const isoFormat = format === "Y-m-d";
          const matcherMonthPosition = isoFormat ? 2 : 1;
          const matcherDayPosition = isoFormat ? 3 : 2;
          const matcherYearPosition = isoFormat ? 1 : 3;
          const defaultDate = new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0);
          const twoDigitYearRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{1,2})$/;
          const fourDigitYearRegex = isoFormat ? /^(\d{4})-(\d{1,2})-(\d{1,2})$/ : /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
          const twoDigitMatcher = date.match(twoDigitYearRegex);
          if (twoDigitMatcher) {
            defaultDate.setFullYear(2000 + parseInt(twoDigitMatcher[matcherYearPosition], 10));
            defaultDate.setMonth(parseInt(twoDigitMatcher[matcherMonthPosition], 10) - 1);
            defaultDate.setDate(parseInt(twoDigitMatcher[matcherDayPosition], 10));
          } else {
            const fourDigitMatcher = date.match(fourDigitYearRegex);
            if (fourDigitMatcher) {
              defaultDate.setFullYear(parseInt(fourDigitMatcher[matcherYearPosition], 10));
              defaultDate.setMonth(parseInt(fourDigitMatcher[matcherMonthPosition], 10) - 1);
              defaultDate.setDate(parseInt(fourDigitMatcher[matcherDayPosition], 10));
            }
          }
          return defaultDate;
        }
      }
    };
  }
});

Vue.filter('nmrDateFormat', (dateISO: string, na = true) => {
  if (!dateISO) return na ? 'na' : '';
  const date = new Date(dateISO);
  return ('0' + (date.getUTCMonth() + 1)).slice(-2) + '.' + ('0' + date.getUTCDate()).slice(-2) + '.' + date.getUTCFullYear();
});
Vue.filter('invoiceEndDate', (invoiceStartDate: string) => {
  const date = convertDateStringToUTCDate(invoiceStartDate);
  if (!date) {
    return null;
  }
  date.setDate(date.getDate() + 6);
  return date.toISOString();
});
Vue.filter('weekFormat', (dateISO: string, na = true) => {
  if (!dateISO) return na ? 'na' : '';
  return format(dateISO, 'MMMM D');
});
// Used when the datetime pretends like the current time zone is UTC
Vue.filter('nmrDateTimeFormat', (dateISO: string) => {
  if (!dateISO) return 'na';
  return format(dateISO, 'MM.DD.YY - hh:mm a');
});
// Used when the date is a true ISO datetime with the appropriate time zone
Vue.filter('nmrDateTime', (dateISO: string) => {
  if (!dateISO) return 'na';
  return DateTime.fromISO(dateISO).toFormat('MM.dd.yy - hh:mm a');
});
Vue.filter('utcDateTimeFormat', (dateISO: string) => {
  const date = convertDateStringToUTCDate(dateISO);
  if (!date) return 'na';
  return format(date, 'MM.DD.YY - hh:mm a');
});
Vue.filter('invoiceStatus', (statusId: number) => {
  if (statusId && statusId > 0) {
    switch (statusId) {
      case InvoiceStatus.SUBMITTED:
        return 'Submitted';
      case InvoiceStatus.PARTIALLY_PAID:
        return 'Partially Paid';
      case InvoiceStatus.FULLY_PAID:
        return 'Fully Paid';
      case InvoiceStatus.IN_REVISION:
        return 'In Revision';
      case InvoiceStatus.POTENTIAL:
        return 'Pre-Invoice';
      default:
        return 'Not Yet Submitted';
    }
  }
  return 'Pre-Invoice';
});
Vue.filter('facilityPortalInvoiceStatus', (statusId: number) => {
  if (statusId && statusId > 0) {
    switch (statusId) {
      case InvoiceStatus.SUBMITTED:
        return 'Not Yet Paid';
      case InvoiceStatus.PARTIALLY_PAID:
        return 'Partially Paid';
      case InvoiceStatus.FULLY_PAID:
        return 'Fully Paid';
      case InvoiceStatus.IN_REVISION:
        return 'Not Yet Paid';
      default:
        return 'Not Yet Paid';
    }
  }
  return 'Pre-Invoice';
});
Vue.filter('considerateCurrency', (quantity: any, transactionTypeId: any) => {
  if (transactionTypeId === 11) {
    return (Vue as any).options.filters.currency(quantity,'$',3);
  } else {
    return (Vue as any).options.filters.currency(quantity);
  }
});
Vue.filter('round', (value:number, digits: number) => {
  const multiplier = Math.pow(10, digits || 0);
  return Math.round(value * multiplier) / multiplier;
});
Vue.filter('percentage', (val: number) => {
  return (val * 100).toFixed(2) + '%'
});
Vue.filter('fullName', (user: any) => {
  return user.firstName + ' ' + user.lastName;
});
Vue.filter('multiSpecialty', (specialtyContainer: any[]) => {
  return Array.from(new Set<string>(specialtyContainer.map(specialty => specialty.specialtyName))).join(", ");
});
Vue.component('switch-control', ToggleSwitchControl)
Vue.component('checkbox-control', CheckboxControl)

Vue.config.productionTip = false;
/* tslint:disable */
// tslint:disable-next-line
new Vue({
  el: '#app',
  router,
  store,
  components: { App },
  template: '<App/>'
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/main.ts